/*
 * Global Cart Request Queue (v2)
 *
 * Purpose:
 *   Survive Shopify cart-API rate limiting AND Cloudflare bot-challenge mode.
 *   Cloudflare auto-unblocks after ~10 min, so we ride out the window instead
 *   of forcing the user to refresh.
 *
 * Pipeline (in order):
 *   1. Match /cart/*.js requests (fetch only — XHR pass-through, TODO).
 *   2. In-flight coalescing for GET /cart.js — concurrent reads share one network call.
 *   3. Circuit breaker — when blocked, fast-fail everything for 10 minutes
 *      (persisted to localStorage so a page refresh doesn't reset the breaker
 *      and re-trigger CF before its window naturally expires). While the
 *      circuit is open, a single auto-probe runs every PROBE_INTERVAL_MS to
 *      detect early recovery (e.g. user passed CF interactive challenge by
 *      navigating to a fresh page); on success the circuit closes immediately.
 *   4. Serial queue, MIN_GAP_MS between requests.
 *   5. Detect block on response: 429 / 403 / 503 / 520-525 -> retry then trip.
 *   6. On breaker trip / circuit-open fast-fail: dispatch `cart:rate-limited`
 *      and show auto-hiding toast — but ONLY if the failure occurred within
 *      USER_ACTION_WINDOW_MS of a real user interaction (click/touch/keydown).
 *      Background traffic (gift sync, Seel polling, drawer auto-refresh on
 *      page load) stays silent. Toasts are throttled to TOAST_MIN_GAP_MS to
 *      avoid spam on rapid clicks.
 *
 * Must load SYNCHRONOUSLY in <head> before any /cart/*.js fetch.
 */
(function () {
  if (window.__cartQueueInstalled) return;
  window.__cartQueueInstalled = true;

  // ── Tunables ───────────────────────────────────────────────────────────
  var MIN_GAP_MS         = 300;        // Between two cart requests
  var MAX_RETRIES        = 2;          // Per-job retries on transient block
  var MAX_RETRY_AFTER_S  = 8;          // Retry-After above this -> treat as persistent
  var CIRCUIT_OPEN_MS    = 10 * 60_000;// Default breaker window — matches empirical CF unblock time
  var COALESCE_WINDOW_MS = 1000;       // Concurrent GET /cart.js share within this
  var TOAST_VISIBLE_MS   = 3200;       // Toast auto-hides after this
  var TOAST_MIN_GAP_MS   = 3000;       // Minimum gap between consecutive toasts (anti-spam on rapid clicks)
  var INITIAL_GRACE_MS   = 1500;       // No toast in this window after script init — suppresses page-load auto fetches
  var PROBE_INTERVAL_MS  = 60_000;     // While circuit is open, probe every N ms to detect early recovery
  var LS_KEY             = '__cartCircuitUntil';

  // Responses that signal we're being blocked (rate limit / CF challenge / WAF)
  var BLOCKED_STATUSES = { 429: 1, 403: 1, 503: 1, 520: 1, 521: 1, 522: 1, 523: 1, 524: 1, 525: 1 };

  // ── State ─────────────────────────────────────────────────────────────
  var queue = [];
  var running = false;
  var pendingMutations = 0;
  var lastFinishedAt = 0;
  var circuitOpenUntil = 0;
  var lastToastAt = 0;
  var lastCartActionAt = 0;
  var pageLoadAt = Date.now();
  var inflightCartJs = null; // { promise, expiresAt }
  var probeTimer = null;
  var probeInFlight = false;
  var probeCount = 0; // For debugging via state()

  try {
    var saved = parseInt(localStorage.getItem(LS_KEY) || '0', 10);
    if (saved > Date.now()) {
      circuitOpenUntil = saved;
      // Page loaded with persistent circuit — start probing right away in
      // case CF / Shopify already lifted the block while we were away.
      schedulePostTripProbe();
    }
  } catch (_) {}

  // Toast only fires when the user clicks a real cart-action element
  // (Add to Cart, qty +/-, remove, cart-editor Save, etc.) — NOT any random
  // click on the page. This matches the product requirement: tell the user
  // their action was blocked, but don't bother them for background traffic
  // (Seel polling, gift sync, drawer auto-refresh, third-party app fetches).
  //
  // To extend: add `data-cart-action` attribute to any custom button.
  var CART_ACTION_SELECTOR = [
    '[name="add"]',                       // Shopify standard ATC button
    '#AddToCart',
    '.single-add-to-cart-button',
    '.add-to-cart, .add-to-cart-btn',
    '.product-card--add-to-cart-button',
    '.quick-add, .quick-action',
    '.pdp-atc',                           // Curtain PDP ATC button (v2/v6/v8/v11/v12/v13)
    '#atc-btn, #buy-now-btn',             // Curtain PDP ATC by id
    '[onclick*="addToCartQuick"]',        // product-bought-together inline handler
    '#fsp-add-to-cart-btn',
    '.global-plus, .global-minus',
    'input.qty, input[name="updates[]"]',
    '#Cart-Drawer .quantity input',
    '#Cart .quantity input',
    '.remove[data-index]',
    '.is-remove-gift',
    '#cdv2-editor-save',
    '.cdv2-edit-btn',
    '[data-cart-action]'
  ].join(',');

  // Tag a fetch as user-initiated only if it was started within this many ms
  // of a real cart-action click. The chain is "click handler -> fetch", which
  // is synchronous in practice (< 100ms). 500ms is generous but still tight
  // enough to exclude downstream async events (gift sync, cart:refreshed
  // chains) that fire seconds after the original click.
  var CART_ACTION_SYNC_MS = 500;

  function recordCartAction(e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest(CART_ACTION_SELECTOR)) {
      lastCartActionAt = Date.now();
    }
  }
  ['click', 'touchend', 'keydown'].forEach(function (evt) {
    document.addEventListener(evt, recordCartAction, { capture: true, passive: true });
  });

  // Captured at fetch INVOCATION time (not at failure time). Carried through
  // the queue so a request that takes 3s to fail still toasts based on whether
  // it was started by a user click.
  function isFetchUserInitiated() {
    if (Date.now() - pageLoadAt < INITIAL_GRACE_MS) return false;
    return Date.now() - lastCartActionAt < CART_ACTION_SYNC_MS;
  }

  // Shopify's routes.cart_change_url may be /cart/change without .js.
  // Queue both forms so quantity changes cannot race gift reconciliation.
  var CART_RE = /\/cart(?:\.js|\/(?:add|change|update|clear)(?:\.js)?)(?:[?#]|$)/i;
  var CART_JS_READ_RE = /\/cart\.js(?:[?#]|$)/i;

  // ── Helpers ───────────────────────────────────────────────────────────
  function isCartUrl(url) {
    if (!url) return false;
    var s = typeof url === 'string' ? url : (url.url || String(url));
    return CART_RE.test(s);
  }
  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && input.url) return input.url;
    return String(input);
  }
  function methodOf(input, init) {
    if (init && init.method) return String(init.method).toUpperCase();
    if (input && input.method) return String(input.method).toUpperCase();
    return 'GET';
  }
  function isCartJsRead(input, init) {
    return methodOf(input, init) === 'GET' && CART_JS_READ_RE.test(urlOf(input));
  }
  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }
  function circuitIsOpen() {
    return Date.now() < circuitOpenUntil;
  }

  function syntheticBlockedResponse() {
    return new Response(
      JSON.stringify({ status: 429, message: 'cart-queue: circuit open' }),
      { status: 429, statusText: 'Too Many Requests (circuit open)', headers: { 'Content-Type': 'application/json' } }
    );
  }

  function tripCircuit(retryAfterS) {
    var ms = CIRCUIT_OPEN_MS;
    if (retryAfterS && !isNaN(retryAfterS)) {
      ms = Math.max(ms, Math.min(retryAfterS * 1000, 15 * 60_000));
    }
    var newUntil = Date.now() + ms;
    if (newUntil > circuitOpenUntil) {
      circuitOpenUntil = newUntil;
      try { localStorage.setItem(LS_KEY, String(circuitOpenUntil)); } catch (_) {}
    }
    console.warn('[cart-queue] circuit OPEN for', Math.round(ms / 1000), 's');
    schedulePostTripProbe();
  }

  function closeCircuit() {
    circuitOpenUntil = 0;
    if (probeTimer) { clearTimeout(probeTimer); probeTimer = null; }
    try { localStorage.removeItem(LS_KEY); } catch (_) {}
  }

  // ── Auto-probe: detect early recovery while circuit is open ───────────
  // Every PROBE_INTERVAL_MS while circuit is open, send a single GET /cart.js
  // directly via native fetch (bypassing our queue + circuit gate). If CF /
  // Shopify lifted the block — e.g. user passed an interactive CF challenge
  // by navigating to a fresh page — we close the circuit early so cart
  // operations resume without waiting out the full 10-minute timer.
  function schedulePostTripProbe() {
    if (probeTimer) return;
    if (!circuitIsOpen()) return;
    probeTimer = setTimeout(runProbe, PROBE_INTERVAL_MS);
  }

  function runProbe() {
    probeTimer = null;
    if (!circuitIsOpen()) return;
    if (probeInFlight) { schedulePostTripProbe(); return; }
    probeInFlight = true;
    probeCount++;

    nativeFetch('/cart.js', { method: 'GET', credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) {
        probeInFlight = false;
        if (res.ok) {
          console.info('[cart-queue] probe succeeded — circuit closing early after', probeCount, 'probe(s)');
          closeCircuit();
        } else {
          // Still blocked (429/403/etc) or other failure — wait and try again
          schedulePostTripProbe();
        }
      })
      .catch(function () {
        probeInFlight = false;
        schedulePostTripProbe();
      });
  }

  // ── Toast (auto-hiding, throttled to avoid spam on rapid clicks) ──────
  function showToast(msg) {
    var now = Date.now();
    if (now - lastToastAt < TOAST_MIN_GAP_MS) return;
    lastToastAt = now;

    var el = document.getElementById('cdv2-toast');
    if (el) {
      el.textContent = msg;
      el.classList.add('isvisible');
      clearTimeout(el._cqTimer);
      el._cqTimer = setTimeout(function () { el.classList.remove('isvisible'); }, TOAST_VISIBLE_MS);
    } else {
      console.warn('[cart-queue]', msg);
    }
  }


  // ── Network attempt ───────────────────────────────────────────────────
  var nativeFetch = window.fetch.bind(window);

  function attempt(input, init) {
    return nativeFetch(input, init).then(function (res) {
      if (!BLOCKED_STATUSES[res.status]) return res;
      var ra = parseFloat(res.headers.get('Retry-After'));
      var err = new Error('blocked');
      err.isBlocked = true;
      err.status = res.status;
      err.retryAfter = isNaN(ra) ? null : ra;
      err.response = res;
      throw err;
    });
  }

  async function runOne(job) {
    if (circuitIsOpen()) {
      job.resolve(syntheticBlockedResponse());
      return;
    }

    var gap = MIN_GAP_MS - (Date.now() - lastFinishedAt);
    if (gap > 0) await sleep(gap);

    var lastErr = null;
    for (var i = 0; i <= MAX_RETRIES; i++) {
      try {
        var res = await attempt(job.input, job.init);
        lastFinishedAt = Date.now();
        job.resolve(res);
        return;
      } catch (e) {
        lastErr = e;
        if (!e.isBlocked) break;
        if (i === MAX_RETRIES) break;
        var waitS = e.retryAfter;
        if (waitS != null && waitS > MAX_RETRY_AFTER_S) break;
        if (waitS == null || isNaN(waitS)) waitS = Math.pow(2, i); // 1s, 2s
        console.warn('[cart-queue] blocked', e.status, 'on', urlOf(job.input), '— retry in', waitS, 's');
        await sleep(waitS * 1000);
      }
    }

    lastFinishedAt = Date.now();
    if (lastErr && lastErr.isBlocked) {
      tripCircuit(lastErr.retryAfter);
      // Toast only if THIS fetch was tagged as user-initiated when it was
      // enqueued. Captured at invocation, not at failure — so a 3-second
      // retry chain still toasts based on the original click context.
      if (job.userTagged) {
        showToast('Too many requests, please wait a moment.');
      }
      document.dispatchEvent(new CustomEvent('cart:rate-limited', {
        detail: { url: urlOf(job.input), status: lastErr.status, retryAfter: lastErr.retryAfter, circuitOpenUntil: circuitOpenUntil }
      }));
      job.resolve(lastErr.response);
    } else {
      job.reject(lastErr || new Error('cart-queue: unknown error'));
    }
  }

  async function pump() {
    if (running) return;
    running = true;
    try {
      while (queue.length) await runOne(queue.shift());
    } finally {
      running = false;
    }
  }

  // ── Intercepted fetch ─────────────────────────────────────────────────
  window.fetch = function (input, init) {
    if (!isCartUrl(input)) return nativeFetch(input, init);

    // Capture at invocation time — when the click handler synchronously
    // called fetch. This survives the queue + retry delays so toast still
    // fires for the right cause at failure time.
    var userTagged = isFetchUserInitiated();

    if (circuitIsOpen()) {
      // Fast-fail. Toast only when this fetch was directly triggered by a
      // user cart-action click. Everything else (background reads, gift
      // sync chain, 3rd-party app polling) stays silent.
      if (userTagged) {
        showToast('Too many requests, please wait a moment.');
      }
      return Promise.resolve(syntheticBlockedResponse());
    }

    // Coalesce concurrent GET /cart.js reads into a single network call.
    // Multiple subscribers each get an independent clone of the response.
    if (isCartJsRead(input, init)) {
      if (inflightCartJs && Date.now() < inflightCartJs.expiresAt) {
        return inflightCartJs.promise.then(function (res) { return res.clone(); });
      }
      var p = new Promise(function (resolve, reject) {
        queue.push({ input: input, init: init, resolve: resolve, reject: reject, userTagged: userTagged });
        pump();
      });
      inflightCartJs = {
        promise: p,
        expiresAt: Date.now() + COALESCE_WINDOW_MS
      };
      // Clear when settled (success or fail) — but keep cache window for late subscribers
      p.finally(function () {
        // Keep inflightCartJs entry so subscribers within COALESCE_WINDOW_MS
        // still hit the cache, but clear it after the window so we don't
        // serve stale data after cart mutations.
        var captured = inflightCartJs;
        setTimeout(function () {
          if (inflightCartJs === captured) inflightCartJs = null;
        }, COALESCE_WINDOW_MS);
      });
      return p.then(function (res) { return res.clone(); });
    }

    // Cart mutations: invalidate any cached /cart.js so the next read is fresh
    inflightCartJs = null;
    pendingMutations += 1;
    return new Promise(function (resolve, reject) {
      queue.push({ input: input, init: init, resolve: resolve, reject: reject, userTagged: userTagged });
      pump();
    }).then(function (response) {
      pendingMutations -= 1;
      return response;
    }, function (error) {
      pendingMutations -= 1;
      throw error;
    });
  };

  // ── Debug API ─────────────────────────────────────────────────────────
  window.__cartQueue = {
    state: function () {
      return {
        queued: queue.length,
        running: running,
        pendingMutations: pendingMutations,
        circuitOpen: circuitIsOpen(),
        circuitOpenUntil: circuitOpenUntil,
        msUntilClose: Math.max(0, circuitOpenUntil - Date.now()),
        inflightCartJs: !!inflightCartJs,
        probeScheduled: !!probeTimer,
        probeCount: probeCount
      };
    },
    reset: function () {
      closeCircuit();
      probeCount = 0;
      console.info('[cart-queue] reset');
    }
  };
})();
